import React from 'react';
import { 
  Users, 
  DollarSign, 
  Calendar,
  Package,
  CreditCard,
  CheckCircle,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';

const KeyStatistics = ({ analytics }) => {
  const overview = analytics?.overview || {};
  const bookingsAnalytics = analytics?.bookings_analytics || {};
  const revenueAnalytics = analytics?.revenue_analytics || {};
  const userAnalytics = analytics?.user_analytics || {};
  const vendorAnalytics = analytics?.vendor_analytics || {};

  // Helper function to format currency
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === '') return '$0.00';
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : Number(amount);
    if (isNaN(numAmount) || !isFinite(numAmount)) return '$0.00';
    return `$${numAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Key statistics
  const keyStats = [
    {
      key: 'total-revenue',
      title: 'Total Revenue',
      value: formatCurrency(overview.total_revenue || revenueAnalytics.total_revenue || 0),
      icon: DollarSign,
      variant: 'success',
      change: revenueAnalytics.revenue_growth || null
    },
    {
      key: 'total-bookings',
      title: 'Total Bookings',
      value: overview.total_bookings || bookingsAnalytics.total || 0,
      icon: Calendar,
      variant: 'default',
      change: bookingsAnalytics.growth_rate || null
    },
    {
      key: 'total-users',
      title: 'Total Users',
      value: overview.total_users || userAnalytics.total_users || 0,
      icon: Users,
      variant: 'info',
      change: userAnalytics.growth_rate || null
    },
    {
      key: 'total-vendors',
      title: 'Total Vendors',
      value: overview.total_vendors || vendorAnalytics.total_vendors || 0,
      icon: Package,
      variant: 'warning',
      change: vendorAnalytics.growth_rate || null
    },
    {
      key: 'platform-fees',
      title: 'Platform Fees',
      value: formatCurrency(overview.platform_fees || revenueAnalytics.platform_fees || 0),
      icon: CreditCard,
      variant: 'default'
    },
    {
      key: 'completed-bookings',
      title: 'Completed',
      value: overview.completed_bookings || bookingsAnalytics.by_status?.completed || 0,
      icon: CheckCircle,
      variant: 'success'
    }
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
      {keyStats.map((stat) => {
        const Icon = stat.icon;
        return (
          <div key={stat.key} className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                stat.variant === 'success' ? 'bg-green-50' :
                stat.variant === 'info' ? 'bg-blue-50' :
                stat.variant === 'warning' ? 'bg-yellow-50' :
                'bg-gray-50'
              }`}>
                <Icon className={`w-5 h-5 ${
                  stat.variant === 'success' ? 'text-green-600' :
                  stat.variant === 'info' ? 'text-blue-600' :
                  stat.variant === 'warning' ? 'text-yellow-600' :
                  'text-gray-600'
                }`} />
              </div>
              {stat.change !== null && stat.change !== undefined && (
                <div className={`flex items-center gap-1 text-xs ${
                  stat.change >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {stat.change >= 0 ? (
                    <ArrowUpRight className="w-3 h-3" />
                  ) : (
                    <ArrowDownRight className="w-3 h-3" />
                  )}
                  <span>{Math.abs(stat.change).toFixed(1)}%</span>
                </div>
              )}
            </div>
            <p className="text-sm text-gray-600 mb-1">{stat.title}</p>
            <p className="text-xl sm:text-2xl font-bold text-gray-900">{stat.value}</p>
          </div>
        );
      })}
    </div>
  );
};

export default KeyStatistics;

